import { useCallback, useLayoutEffect, useState } from 'react';
import { useTabs } from '../../../context/TabsContext';

/**
 * Keep Mission Full View + mission-doc stack on the active tab (persisted).
 * SprintDetail stays mounted across tab switches (stable key), so local-only
 * open state would leak between tabs — this syncs both directions.
 *
 * Hydration runs in useLayoutEffect so the first painted frame after a tab
 * switch already matches the tab surface (no board → MFV → doc flip).
 */
export function useTabMissionOverlay(detailSidebarTaskId, setDetailSidebarTaskId) {
  const { activeTab, activeTabId, updateTab } = useTabs();
  const [skipSurfaceAnimation, setSkipSurfaceAnimation] = useState(false);

  // Hydrate local open-mission from the active tab before paint
  useLayoutEffect(() => {
    const next = activeTab?.openMissionId ?? null;
    if (detailSidebarTaskId === next) return;
    setSkipSurfaceAnimation(true);
    setDetailSidebarTaskId(next);
  }, [activeTabId, activeTab?.openMissionId, detailSidebarTaskId, setDetailSidebarTaskId]);

  const consumeSkipSurfaceAnimation = useCallback(() => {
    setSkipSurfaceAnimation(false);
  }, []);

  const openMission = useCallback((taskId, options) => {
    const docId = options && typeof options === 'object' ? options.docId : null;
    const stack = typeof docId === 'string' && docId ? [docId] : [];
    // A doc opened from the card is not sitting on top of Mission Full View.
    // Closing it should return to the board, not reveal the mission.
    const alreadyThisMission =
      activeTab?.openMissionId === taskId || detailSidebarTaskId === taskId;
    setDetailSidebarTaskId(taskId);
    if (!activeTabId) return;
    updateTab(activeTabId, {
      openMissionId: taskId,
      missionDocStack: stack,
      missionDocScrollById: {},
      missionDocDirect: !!docId && !alreadyThisMission,
    });
  }, [activeTab?.openMissionId, activeTabId, detailSidebarTaskId, setDetailSidebarTaskId, updateTab]);

  const closeMission = useCallback((options) => {
    if (options?.immediate) setSkipSurfaceAnimation(true);
    setDetailSidebarTaskId(null);
    if (!activeTabId) return;
    updateTab(activeTabId, {
      openMissionId: null,
      missionDocStack: [],
      missionDocScrollById: {},
      missionDocDirect: false,
    });
  }, [activeTabId, setDetailSidebarTaskId, updateTab]);

  const setMissionDocStack = useCallback((stack) => {
    if (!activeTabId) return;
    const next = Array.isArray(stack) ? stack : [];
    if (next.length === 0 && activeTab?.missionDocDirect) {
      closeMission({ immediate: true });
      return;
    }
    updateTab(activeTabId, { missionDocStack: next });
  }, [activeTab?.missionDocDirect, activeTabId, closeMission, updateTab]);

  // Prefer the tab's stack whenever the tab has a mission surface — don't wait
  // for local state to catch up after a tab switch.
  const restoredDocStack = activeTab?.openMissionId
    ? (activeTab.missionDocStack || [])
    : [];

  return {
    openMission,
    closeMission,
    setMissionDocStack,
    restoredDocStack,
    skipSurfaceAnimation,
    consumeSkipSurfaceAnimation,
    tabOpenMissionId: activeTab?.openMissionId ?? null,
    missionDocDirect: !!activeTab?.missionDocDirect,
  };
}

export default useTabMissionOverlay;
